import React, { useEffect, useMemo, useState } from "react";
import API from "../../../util/API";
import { Link } from "react-router-dom";
import ADMIN_PATHS from "../ADMIN_PATHS";
import AdminPageShell from "../components/AdminPageShell";
import { Ban, CheckCircle2, Eye, Trash2 } from "lucide-react";

const cx = (...arr) => arr.filter(Boolean).join(' ');

const apiErrorMessage = (err, fallback) =>
  err?.response?.data?.error?.message ||
  err?.response?.data?.message ||
  err?.message ||
  fallback;

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [blockBusyId, setBlockBusyId] = useState(null);

  const getUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await API.get("/user");
      setUsers(response?.data?.data || []);
    } catch (error) {
      setUsers([]);
      setError(apiErrorMessage(error, "Failed to fetch users"));
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const handleDelete = async (userId) => {
    if (!userId) return;
    const confirmed = window.confirm("Delete this user? This action cannot be undone.");
    if (!confirmed) return;

    try {
      setDeletingId(userId);
      setError(null);
      await API.delete(`/user/${encodeURIComponent(userId)}`);
      setUsers((prev) => prev.filter((u) => u?._id !== userId));
    } catch (deleteError) {
      setError(apiErrorMessage(deleteError, "Failed to delete user"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleBlock = async (user) => {
    const userId = user?._id;
    if (!userId) return;
    const confirmed = window.confirm(`Block ${user.email || "this user"}? Their sessions are revoked immediately and they cannot sign in.`);
    if (!confirmed) return;
    const reason = window.prompt("Reason for blocking (stored for admins only):", "");
    if (reason === null) return;
    if (!reason.trim()) {
      setError("A block reason is required.");
      return;
    }

    try {
      setBlockBusyId(userId);
      setError(null);
      const response = await API.post(`/user/${encodeURIComponent(userId)}/block`, { reason: reason.trim() });
      const data = response?.data?.data || {};
      setUsers((prev) =>
        prev.map((u) => (u?._id === userId ? { ...u, isBlocked: true, blockedReason: data.blockedReason || reason.trim() } : u))
      );
    } catch (blockError) {
      setError(apiErrorMessage(blockError, "Failed to block user"));
    } finally {
      setBlockBusyId(null);
    }
  };

  const handleUnblock = async (user) => {
    const userId = user?._id;
    if (!userId) return;
    const confirmed = window.confirm("Unblock this account? The user can sign in again immediately.");
    if (!confirmed) return;

    try {
      setBlockBusyId(userId);
      setError(null);
      await API.post(`/user/${encodeURIComponent(userId)}/unblock`, {});
      setUsers((prev) =>
        prev.map((u) => (u?._id === userId ? { ...u, isBlocked: false, blockedReason: "" } : u))
      );
    } catch (unblockError) {
      setError(apiErrorMessage(unblockError, "Failed to unblock user"));
    } finally {
      setBlockBusyId(null);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const hay = [
        u?._id,
        u?.name,
        u?.username,
        u?.firstName,
        u?.lastName,
        u?.email,
        u?.phone,
        u?.address,
        u?.role,
        u?.googleId,
        u?.settings?.currency,
        u?.settings?.shipto,
        u?.settings?.country,
        u?.settings?.language,
        u?.settings?.theme,
      ]
        .filter(Boolean)
        .join(" | ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [users, query]);

  const renderUserRow = user => (
    <tr key={user._id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3 font-semibold text-slate-900">
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0">{user.name || [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username || "—"}</span>
          {user.isBlocked ? (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-700"
              title={user.blockedReason ? `Reason: ${user.blockedReason}` : "Blocked"}
            >
              <Ban size={11} />
              Blocked
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-600 max-w-[180px] truncate" title={user.username || ""}>{user.username || "—"}</td>
      <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{user.firstName || "—"}</td>
      <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{user.lastName || "—"}</td>
      <td className="px-4 py-3 text-slate-600 max-w-[240px] truncate" title={user.email || ""}>{user.email || "—"}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{user.role || "—"}</td>
      <td className="px-4 py-3 text-slate-600 hidden xl:table-cell whitespace-nowrap">{user?.settings?.country || "—"}</td>
      <td className="px-4 py-3 text-slate-600 hidden xl:table-cell whitespace-nowrap">
        {user?.createdAt ? new Date(user.createdAt).toLocaleString() : "—"}
      </td>
      <td className="px-4 py-3 text-right">
        {user.isBlocked ? (
          <button
            type="button"
            onClick={() => handleUnblock(user)}
            disabled={blockBusyId === user._id || deletingId === user._id}
            className={cx(
              "inline-flex items-center gap-2 px-3 py-2 rounded-2xl border text-sm font-semibold transition-colors mr-2",
              blockBusyId === user._id
                ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            )}
          >
            <CheckCircle2 size={16} className={blockBusyId === user._id ? "text-slate-400" : "text-emerald-600"} />
            <span className="hidden sm:inline">Unblock</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => handleBlock(user)}
            disabled={blockBusyId === user._id || deletingId === user._id}
            className={cx(
              "inline-flex items-center gap-2 px-3 py-2 rounded-2xl border text-sm font-semibold transition-colors mr-2",
              blockBusyId === user._id
                ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
            )}
          >
            <Ban size={16} className={blockBusyId === user._id ? "text-slate-400" : "text-amber-600"} />
            <span className="hidden sm:inline">Block</span>
          </button>
        )}
        <button
          type="button"
          onClick={() => handleDelete(user._id)}
          disabled={deletingId === user._id}
          className={cx(
            "inline-flex items-center gap-2 px-3 py-2 rounded-2xl border text-sm font-semibold transition-colors mr-2",
            deletingId === user._id
              ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
              : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
          )}
        >
          <Trash2 size={16} className={deletingId === user._id ? "text-slate-400" : "text-rose-600"} />
          <span className="hidden sm:inline">Delete</span>
        </button>
        <Link
          to={`${ADMIN_PATHS.USER_DETAILS}?id=${encodeURIComponent(user._id)}`}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-slate-200 bg-white text-slate-800 text-sm font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors"
        >
          <Eye size={16} className="text-slate-600" />
          <span className="hidden sm:inline">View</span>
        </Link>
      </td>
    </tr>
  );

  return (
    <AdminPageShell
      title="User Management"
      description="Manage user accounts, roles, and verification status."
    >
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
        <div className="min-w-0">
          <div className="text-sm text-slate-600">
            {loading ? "Loading users…" : `${filteredUsers.length} users`}
            {error ? <span className="text-rose-600"> · {error}</span> : null}
          </div>
        </div>
        <div className="w-full md:w-[380px]">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, username, id, role…"
            className="w-full px-3 py-2 rounded-2xl border border-slate-200 focus:ring-2 focus:ring-primary/10 focus:border-primary outline-none"
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-black tracking-wide text-slate-600 uppercase">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Username</th>
                <th className="px-4 py-3 hidden lg:table-cell">First</th>
                <th className="px-4 py-3 hidden lg:table-cell">Last</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3 hidden xl:table-cell">Country</th>
                <th className="px-4 py-3 hidden xl:table-cell">Created At</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>{filteredUsers.map(renderUserRow)}</tbody>
          </table>
        </div>
      </Card>
    </AdminPageShell>
  );
};

export default UserList;